import React, { useRef } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import LoadingView from '../../shared/components/LoadingView';
import useDocumentLibrary from '../hooks/useDocumentLibrary';
import useTags from '../hooks/useTags';
import DocumentLibraryTable from '../components/document-library/DocumentLibraryTable';

const ACCEPT_TYPES = [
  '.pdf', '.html', '.htm', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv',
  '.jpg', '.jpeg', '.png', '.gif', '.webp',
  'application/pdf', 'text/html', 'text/plain', 'text/csv',
].join(',');

export default function OrganizationDocumentLibraryPage() {
  useDocumentTitle('Document library – Organization settings – Maintella');
  const fileInputRef = useRef(null);
  const {
    items,
    loading,
    error,
    uploading,
    uploadFile,
    deleteItem,
    downloadItem,
    setFieldEngineerVisible,
    setTags,
    retryKnowledgeIndex,
  } = useDocumentLibrary();
  const { tags } = useTags();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      await uploadFile(file);
    } catch {
      // error surfaced via hook
    }
  };

  return (
    <>
      <ObjectTopBar
        title="Document library"
        primaryBtn={{
          btnText: uploading ? 'Uploading…' : 'Upload document',
          btnIcon: 'bi-upload',
          variant: 'primary',
          onClick: () => fileInputRef.current?.click(),
          disabled: uploading,
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_TYPES}
        className="d-none"
        onChange={handleFileChange}
      />

      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Organization-wide documents</h6>
          <p className="text-muted small mb-0">
            Manuals and reference documents shared across every asset. <strong>Shared</strong> makes a
            document available to field engineers on asset pages and enables the AI assistant for PDF,
            HTML, TXT, CSV and Excel files. Scope it as <strong>Global</strong> or limit it with asset
            tags. Asset-specific files are managed on each asset.
          </p>
        </div>

        {loading && items.length === 0 ? (
          <LoadingView
            symbolClass="bi-file-earmark-text"
            title="Loading document library"
            subtitle="Fetching organization documents."
            className="py-5"
          />
        ) : (
          <>
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
            )}
            <DocumentLibraryTable
              items={items}
              onDelete={deleteItem}
              onDownload={downloadItem}
              onSetVisible={setFieldEngineerVisible}
              onSetTags={setTags}
              onRetryIndex={retryKnowledgeIndex}
              tags={tags}
            />
          </>
        )}
      </div>
    </>
  );
}
